import {
  AnalyticsConfig,
  ClusterMapWidget,
  FilterMapDataType,
  FilterMapWidget,
  FilterMapWidgetConfig,
  HeatMapWidget,
  IncidentData,
  MapBaseDataType,
  MapWidget,
  PatternMapWidget,
  Widget,
  WidgetColor,
  WidgetFilterOption
} from '../../../Models';
import {
  WIDGET_FILTER_MAP,
  WIDGET_HEAT_MAP,
  WIDGET_CLUSTER_MAP,
  WIDGET_STACKED_LINE,
  WIDGET_GROUPED_BAR
} from '../../../Constants/Analytics';
import {
  ANALYTICS_DAY_PALETTE,
  ANALYTICS_NIGHT_PALETTE
} from '../../../Constants/Colors';

const PATTER_MAP_LIMIT = 300;

export const getWidgetWithData = (
  config: AnalyticsConfig,
  id: number,
  position: number
): Widget => {
  const widget: Widget = config.widgets.find(
    (widget: Widget) => widget.id === id
  ) as Widget;

  widget.position = position;

  if (widget.component.name.indexOf('_map') === -1) {
    if (
      widget.component.name === WIDGET_STACKED_LINE ||
      widget.component.name === WIDGET_GROUPED_BAR
    ) {
      widget.component.config.data = widget.component.config.data.map(
        (d, index) => ({
          ...d,
          color: {
            day:
              d.color && typeof d.color === 'string'
                ? d.color
                : ANALYTICS_DAY_PALETTE[index % ANALYTICS_DAY_PALETTE.length],
            night:
              d.color && typeof d.color === 'string'
                ? d.color
                : ANALYTICS_NIGHT_PALETTE[
                    index % ANALYTICS_NIGHT_PALETTE.length
                  ]
          }
        })
      );
    }
    return widget;
  } else {
    const mapWidget: MapWidget = widget.component as MapWidget;
    const filter = mapWidget.config.filter;

    let data: IncidentData[] = config.incident_data;

    if (filter) {
      data = data.filter((value: IncidentData) => {
        if (!value[filter.key]) {
          return false;
        }

        const dataValue = value[filter.key];

        // Check if it's the special 'not null' filter
        if (filter.value === 'not null') {
          return dataValue !== null && dataValue !== undefined;
        } else {
          // Check if data is an array: string[]
          if (Array.isArray(dataValue)) {
            return dataValue.includes(filter.value);
          } else {
            return dataValue === filter.value;
          }
        }
      });
    }

    if (widget.component.name === WIDGET_FILTER_MAP) {
      const componentConfig = widget.component.config as FilterMapWidgetConfig;
      const optionKey: string = componentConfig.options_key;
      let colorDict: Record<string, WidgetColor> = {};
      let filterMapOptions: WidgetFilterOption[] = [];

      componentConfig.options.forEach((option, index) => {
        const optionColor = {
          day:
            componentConfig.colors && componentConfig.colors.length > 0
              ? componentConfig.colors[index]
              : ANALYTICS_DAY_PALETTE[index % ANALYTICS_DAY_PALETTE.length],
          night:
            componentConfig.colors && componentConfig.colors.length > 0
              ? componentConfig.colors[index]
              : ANALYTICS_NIGHT_PALETTE[index % ANALYTICS_NIGHT_PALETTE.length]
        };

        colorDict[option] = optionColor;
        filterMapOptions = [
          ...filterMapOptions,
          {
            label: option,
            color: optionColor
          }
        ];
      });

      let mapData: FilterMapDataType[] = [];

      for (let incident of data) {
        if (incident[optionKey]) {
          let values: string[] = [];
          if (Array.isArray(incident[optionKey])) {
            values = incident[optionKey] as string[];
          } else {
            values = [incident[optionKey] as string];
          }

          for (let value of values) {
            mapData.push({
              coords: incident.coords,
              incident_number: incident.incident_number,
              incident_date: incident.incident_date,
              author_name: incident.author_name,
              name: value,
              color: colorDict[value]
            });
          }
        }
      }

      mapWidget.config.mapData = mapData;
      widget.component.config.filterMapOptions = filterMapOptions;

      return { ...widget, component: mapWidget as FilterMapWidget };
    } else {
      const mapData: MapBaseDataType[] = data.map((incident: IncidentData) => ({
        coords: incident.coords,
        incident_date: incident.incident_date,
        incident_number: incident.incident_number,
        author_name: incident.author_name
      }));

      mapWidget.config.mapData = mapData;

      if (widget.component.name === WIDGET_HEAT_MAP) {
        return { ...widget, component: mapWidget as HeatMapWidget };
      } else if (widget.component.name === WIDGET_CLUSTER_MAP) {
        return { ...widget, component: mapWidget as ClusterMapWidget };
      } else {
        // It's a 'pattern_map'
        const limited = mapWidget.config.mapData.length > PATTER_MAP_LIMIT;
        // Limit pattern map data to avoid render issues
        mapWidget.config.mapData = mapWidget.config.mapData.slice(
          -PATTER_MAP_LIMIT
        );
        return {
          ...widget,
          component: { ...(mapWidget as PatternMapWidget), limited }
        };
      }
    }
  }
};
